// Iter 91d — Shared hook for Super Admin submenu reorder.
//
// Reads `settings.submenu_orders[menuKey]` and returns a sort function
// plus a `save(nextOrder)` writer that PATCHes it back through the
// standard `/api/settings` passthrough (no dedicated endpoint required).
//
// Callers pass their default key list; the hook falls back to defaults
// so new tabs surface at the tail after a code deploy without wiping
// any previously saved order.
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

export function useSubmenuOrder(menuKey, defaultKeys) {
  const [savedOrder, setSavedOrder] = useState(null);   // null = still loading
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!menuKey) return;
    api.get("/settings").then((r) => {
      const persisted = r.data?.submenu_orders?.[menuKey];
      setSavedOrder(Array.isArray(persisted) ? persisted : []);
    }).catch(() => setSavedOrder([]));
  }, [menuKey]);

  // Deterministic list order: persisted keys first (in saved order),
  // then any new keys not yet in the saved list (in caller order).
  const orderedKeys = (() => {
    const persisted = savedOrder || [];
    const set = new Set(persisted);
    const trailing = (defaultKeys || []).filter((k) => !set.has(k));
    return [...persisted.filter((k) => (defaultKeys || []).includes(k)), ...trailing];
  })();

  const save = useCallback(async (nextKeys) => {
    if (!menuKey) return;
    setSaving(true);
    try {
      const current = (await api.get("/settings")).data || {};
      const nextSubmenus = { ...(current.submenu_orders || {}), [menuKey]: nextKeys };
      await api.put("/settings", { submenu_orders: nextSubmenus });
      setSavedOrder(nextKeys);
      toast.success("Order saved");
    } catch (e) {
      toast.error("Failed to save order");
    } finally { setSaving(false); }
  }, [menuKey]);

  return { orderedKeys, save, saving, loaded: savedOrder !== null };
}
